import React, { useState } from 'react';
import {
	AuthRoleEnum,
	CurrencyEnum,
	MeasurementSystemEnum,
	MfaMethodEnum,
	OrganizationIndustryEnum,
	OrganizationSignupInput,
	useCreateOrganizationSignupMutation,
} from '@codezo/graphql';
import { Box, Button, Flex, Group, Paper, ScrollArea, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';
import { AuthenticatorSetupStep } from '../components/signup/AuthenticatorSetupStep';
import { CountryOrganizationStep } from '../components/signup/CountryOrganizationStep';
import { EmailVerificationStep } from '../components/signup/EmailVerificationStep';
import { OrganizationSettingsStep } from '../components/signup/OrganizationSettingsStep';
import { PersonalInfoStep } from '../components/signup/PersonalInfoStep';

/**
 * Helper function to determine redirect URL based on user role
 */
function getRedirectUrlForRole(role?: string): string {
	if (role === AuthRoleEnum.Admin) {
		return import.meta.env.VITE_ADMIN_APP_URL || 'http://localhost:3002';
	}
	return import.meta.env.VITE_PUBLIC_APP_URL || 'http://localhost:3000';
}

interface SignupStep {
	id: string;
	title: string;
	description: string;
	component: React.ComponentType<{
		data: Record<string, unknown>;
		onDataChange: (_data: Record<string, unknown>) => void;
		onValidSubmit: (_data: Record<string, unknown>) => void;
		onPrevious: () => void;
		isFirstStep: boolean;
		isLastStep: boolean;
		triggerValidation: number;
	}>;
}

const baseSignupSteps: SignupStep[] = [
	{
		id: 'organization',
		title: 'Organization Information',
		description: 'Tell us about your organization',
		component: CountryOrganizationStep,
	},
	{
		id: 'settings',
		title: 'Settings',
		description: 'Configure your preferences',
		component: OrganizationSettingsStep,
	},
	{
		id: 'personal',
		title: 'Personal Information',
		description: 'Create your account',
		component: PersonalInfoStep,
	},
	{
		id: 'mfa-setup',
		title: 'Security Setup',
		description: 'Set up two-factor authentication',
		component: PersonalInfoStep, // Placeholder, will be replaced dynamically
	},
];

export function SignupPage() {
	const [currentStep, setCurrentStep] = useState(0);
	const [formData, setFormData] = useState<Record<string, unknown>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [triggerValidation, setTriggerValidation] = useState(0);
	const [signupSteps, setSignupSteps] = useState<SignupStep[]>(baseSignupSteps);
	const [organizationCreated, setOrganizationCreated] = useState(false);

	const navigate = useNavigate();
	const [createOrganizationSignup] = useCreateOrganizationSignupMutation();

	const handleSubmitOrganization = async (completeFormData: Record<string, unknown>) => {
		setIsSubmitting(true);

		try {
			// Debug: Log the final form data
			// Removed console.log
			// Removed console.log
			// Removed console.log

			// No need for manual mapping - the values are already in the correct format!

			// Extract address from form data
			const address = completeFormData.address as {
				addressLine1: string;
				addressLine2?: string;
				city: string;
				stateCode?: string;
				postalCode: string;
				countryCode: string;
				googlePlaceId?: string;
				latitude?: number | null;
				longitude?: number | null;
			};

			// Extract business registrations
			const businessRegistrations = completeFormData.businessRegistrations as Array<{
				type: string;
				number: string;
			}>;

			// Transform form data to match OrganizationSignupInput
			const organizationSignupInput: OrganizationSignupInput = {
				organizationName: completeFormData.organizationName as string,
				countryCode: completeFormData.countryCode as string,
				industry: completeFormData.industry
					? (completeFormData.industry as OrganizationIndustryEnum)
					: undefined,
				websiteUrl: completeFormData.websiteUrl
					? (completeFormData.websiteUrl as string)
					: undefined,
				businessRegistrations: businessRegistrations || [],
				address: address
					? {
							addressLine1: address.addressLine1,
							addressLine2: address.addressLine2,
							city: address.city,
							stateCode: address.stateCode,
							postalCode: address.postalCode,
							countryCode: address.countryCode,
							googlePlaceId: address.googlePlaceId,
							latitude: address.latitude,
							longitude: address.longitude,
						}
					: undefined,
				currency: completeFormData.currency as CurrencyEnum,
				timezone: completeFormData.timezone as string,
				measurementSystem: completeFormData.measurementSystem as MeasurementSystemEnum,
				email: completeFormData.email as string,
				password: completeFormData.password as string,
				firstName: completeFormData.firstName as string,
				lastName: completeFormData.lastName as string,
				jobPosition: completeFormData.jobPosition as string,
				role: AuthRoleEnum.Organization,
				mfaMethod: completeFormData.mfaMethod as MfaMethodEnum,
			};

			const { data } = await createOrganizationSignup({
				variables: {
					input: organizationSignupInput,
				},
			});

			if (data?.createOrganizationSignup) {
				const { userId, qrCodeDataURL } = data.createOrganizationSignup;

				// Store necessary data for MFA setup
				sessionStorage.setItem('pendingUserRole', AuthRoleEnum.Organization);

				// Add MFA data to form data
				const mfaData = {
					userId,
					qrCodeDataURL,
					email: completeFormData.email,
				};

				// Update form data with MFA info
				setFormData((prev) => ({ ...prev, ...mfaData }));

				// Mark organization as created
				setOrganizationCreated(true);

				// Update the 4th step with the appropriate MFA component
				const mfaMethod = completeFormData.mfaMethod as MfaMethodEnum;
				const mfaComponent =
					mfaMethod === MfaMethodEnum.Email
						? EmailVerificationStep
						: AuthenticatorSetupStep;

				// Update the last step with the correct MFA component
				setSignupSteps((prev) => {
					const updated = [...prev];
					updated[3] = {
						...updated[3],
						component: mfaComponent,
						title:
							mfaMethod === MfaMethodEnum.Email
								? 'Email Verification'
								: 'Authenticator Setup',
						description:
							mfaMethod === MfaMethodEnum.Email
								? 'Verify your email address'
								: 'Set up your authenticator app',
					};
					return updated;
				});

				// Move to MFA setup step
				setCurrentStep(currentStep + 1);
				setIsSubmitting(false);

				// Reset validation trigger
				setTriggerValidation(0);

				// Don&apos;t continue with the original flow
			}
		} catch {
			// Error handled by mutation onError
			notifications.show({
				title: 'Error',
				message: 'Failed to create organization. Please try again.',
				color: 'red',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleNext = async (stepData: Record<string, unknown>) => {
		const updatedFormData = { ...formData, ...stepData };
		setFormData(updatedFormData);

		// Check if this is the MFA setup completion
		if (stepData.mfaSetupComplete) {
			// MFA setup is complete, redirect to the app
			const authToken = stepData.authToken as string;
			const redirectUrl = getRedirectUrlForRole(AuthRoleEnum.Organization);

			// Save backup codes if they exist
			if (stepData.backupCodes) {
				// In a real app, you might want to show these in a modal first
				// Removed console.log
			}

			// Redirect with the auth token
			window.location.href = `${redirectUrl}/auth/callback?token=${authToken}`;
			return;
		}

		// Check if we're at the personal info step (step 2, index 2)
		if (currentStep === 2 && !organizationCreated) {
			// This is the personal info step, need to create the organization
			await handleSubmitOrganization(updatedFormData);
			return;
		}

		// Normal flow - move to next step
		if (currentStep < signupSteps.length - 1) {
			setCurrentStep(currentStep + 1);
			// Reset validation trigger when moving to next step
			setTriggerValidation(0);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
			// Reset validation trigger when going back
			setTriggerValidation(0);
		}
	};

	const handleStepDataChange = (stepData: Record<string, unknown>) => {
		// Update the accumulated form data in real-time
		setFormData((prev) => ({ ...prev, ...stepData }));
	};

	const handleCentralNext = () => {
		// Trigger validation in the child component
		setTriggerValidation((prev) => prev + 1);
	};

	const CurrentStepComponent = signupSteps[currentStep].component;
	const currentStepInfo = signupSteps[currentStep];

	return (
		<Box h="100vh" style={{ display: 'flex', flexDirection: 'column' }}>
			{/* Fixed header */}
			<Box>
				<Box
					pt={{ base: 'xs', sm: 'xl' }}
					px={{ base: 'xs', sm: 'xl' }}
					maw={800}
					mx="auto"
				>
					<Group justify="space-between" align="flex-start">
						<Box>
							<Title order={2} size="h3" mb="xs">
								{currentStepInfo.title}
							</Title>
							<Text c="dimmed" size="sm">
								{currentStepInfo.description}
							</Text>
						</Box>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.2 }}
						>
							<Text size="sm" c="dimmed" mb="xs">
								Step {currentStep + 1} of {signupSteps.length}
							</Text>
							<Group justify="flex-end">
								<Box display={{ base: 'block', md: 'none' }}>
									<Logo onClick={() => navigate('/')} height={28} />
								</Box>
							</Group>
						</motion.div>
					</Group>
				</Box>
			</Box>

			{/* Main content area with scroll */}
			<ScrollArea flex={1}>
				<Box py={{ base: 'xs', sm: 'xl' }} maw={800} mx="auto" h="100%">
					{/* Step content */}
					<AnimatePresence mode="wait">
						<motion.div
							key={currentStepInfo.id}
							initial={{ opacity: 0, x: 50 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -50 }}
							transition={{ duration: 0.4, ease: 'easeInOut' }}
							style={{ height: '100%' }}
						>
							<Paper
								p={{ base: 'xs', sm: 'xl' }}
								m={{ base: 'xs', sm: 'xl' }}
								radius="md"
								withBorder
								h="100%"
								style={{
									transition: 'box-shadow 0.3s ease',
								}}
							>
								<CurrentStepComponent
									data={formData}
									onDataChange={handleStepDataChange}
									onValidSubmit={handleNext}
									onPrevious={handlePrevious}
									isFirstStep={currentStep === 0}
									isLastStep={currentStep === signupSteps.length - 1}
									triggerValidation={triggerValidation}
								/>
							</Paper>
						</motion.div>
					</AnimatePresence>
				</Box>
			</ScrollArea>

			{/* Footer with stepper and buttons */}
			<Box
				py={{ base: 'xs', sm: 'md' }}
				px={{ base: 'xs', sm: 'xl' }}
				size="xl"
				style={{
					borderTop: '1px solid var(--mantine-color-gray-3)',
				}}
			>
				{/* Full width container with equal columns for true centering */}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr auto 1fr',
						alignItems: 'center',
						gap: '2rem',
						maxWidth: '100%',
						padding: '0 2rem',
					}}
				>
					{/* Left button container - aligned to start */}
					<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
						<motion.div
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
							transition={{ duration: 0.2 }}
						>
							<Button
								variant="subtle"
								onClick={handlePrevious}
								disabled={currentStep === 0}
								style={{
									minWidth: '60px',
									transition: 'all 0.2s ease',
								}}
							>
								Previous
							</Button>
						</motion.div>
					</div>

					{/* Center stepper dashes */}
					<Flex gap={{ base: 'xs', sm: 'sm' }} mx={{ base: 'xs', sm: 'md' }}>
						{signupSteps.map((step, index) => (
							<motion.div
								key={step.id}
								initial={{ scale: 0.8, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								transition={{
									duration: 0.3,
									delay: index * 0.1,
									ease: 'easeOut',
								}}
							>
								<Box
									w={{ base: 40, xs: 60, sm: 80, md: 100 }}
									h={4}
									bg={
										index <= currentStep
											? 'var(--mantine-primary-color-filled)'
											: 'var(--mantine-color-gray-4)'
									}
									style={{
										borderRadius: 2,
										transition: 'all 0.4s ease',
										transform:
											index === currentStep ? 'scale(1.05)' : 'scale(1)',
									}}
								/>
							</motion.div>
						))}
					</Flex>

					{/* Right button container - aligned to end */}
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<motion.div
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
							transition={{ duration: 0.2 }}
						>
							<Button
								onClick={handleCentralNext}
								loading={isSubmitting}
								disabled={isSubmitting}
								style={{
									minWidth: 150,
									transition: 'all 0.2s ease',
								}}
							>
								{(() => {
									// Custom button text based on step
									if (currentStep === 2 && !organizationCreated) {
										// Personal info step - will create organization
										return isSubmitting
											? 'Creating Account...'
											: 'Create Account';
									}
									if (currentStep === signupSteps.length - 1) {
										// Last step
										const currentStepId = signupSteps[currentStep]?.id;
										if (
											currentStepId === 'authenticator-setup' ||
											currentStepId === 'email-verification'
										) {
											return isSubmitting ? 'Verifying...' : 'Complete Setup';
										}
										return isSubmitting ? 'Creating...' : 'Finish';
									}
									return 'Next';
								})()}
							</Button>
						</motion.div>
					</div>
				</div>
			</Box>
		</Box>
	);
}
